import type { ChannelSlice } from '../ChannelSlice';
import type { SymbolPickerElement, WidgetHeaderElement } from '../WidgetModel';
import { COMMON_HEADER_ELEMENTS } from '../WidgetModel/impl';
import type { WidgetTabSlice } from '../WidgetTabSlice';
import { computed, signal } from '@preact/signals-react';
import type { ReadonlySignal, Signal } from '@preact/signals-react';
import type { Instrument } from '@thinkalpha/common/schemas/instruments.js';
import { EmptyArray } from '@thinkalpha/common/util/iterators.js';
import type { OrderStatus } from '@thinkalpha/trading-ws-client/contracts/order.js';
import type { FormattableGridSettings } from '@thinkalpha/ui-types/contracts/display/settings';
import { optional } from 'inversify';
import { createStructuredSelector } from 'reselect';
import { ReactiveInjectable, reacts, inject, injectable } from 'src/features/ioc';
import type { InstrumentType } from 'src/features/symbol-picker/state';
import { riskWidgetsShouldShowNewSymbolPicker } from 'src/lib/featureFlags/flags/riskWidgetsShouldShowNewSymbolPicker';
import { instrumentToString, parseInstrument } from 'src/lib/instrumentUtils';
import type { MessagesWidgetModel } from 'src/models/MessagesWidgetModel';
import { messagesOrderStatuses, normalizeMessagesStatusFilter } from 'src/models/orderStatusFilter';
import { MessagesGridDefaults } from 'src/routes/widgets/MessagesWidget/types';
import type { TradingSocketConnectionStatus, TradingSocketService } from 'src/services/TradingSocketService';
import type { WorkspaceService } from 'src/services/WorkspaceService';
import type { RootState } from 'src/store';
import { updateWidget } from 'src/store/actions/widgetAndChannel';
import { userDoubleClickedSymbolFromTable } from 'src/store/actions/widgets/linking';
import { updateMessagesInstrument } from 'src/store/actions/widgets/messages';
import type { MessagesWidgetViewModel } from 'src/store/types';
import type { ReactBindings } from 'src/types/bindings';
import type { ConnectionStatusSignal } from 'src/types/EventBus';
import { EventBusBuilder, MergedEventBus } from 'src/types/EventBus/EventBus';

const makeSelectWidgetData = (tabId: string) =>
    createStructuredSelector({
        tab: (state: RootState) => state.tab.byId[tabId],
    });

@injectable()
export class MessagesWidgetModelImpl extends ReactiveInjectable implements MessagesWidgetModel {
    #channelSlice: ChannelSlice;
    #widgetTabSlice: WidgetTabSlice;

    #instrument = signal<Instrument>(parseInstrument(''));
    #allowedInstrumentTypes: ReadonlySignal<InstrumentType[]> = signal(['equity', 'options']);
    #accountIds!: Signal<string[] | null>;
    #statusFilterSignal!: Signal<OrderStatus[] | null>;
    #selectableOrderStatuses = signal(messagesOrderStatuses);
    #isAccountSelectionValid: Signal<boolean> = signal(true);
    #formattingSettings!: Signal<FormattableGridSettings | null>;
    #isGridSettingsDialogOpen: Signal<boolean> = signal(false);
    #tradingSocketService: TradingSocketService;
    #connectionStatus!: ReadonlySignal<TradingSocketConnectionStatus>;

    headerElements!: WidgetHeaderElement[];

    eventBus!: MessagesWidgetModel['eventBus'];

    #buildEventBus(): MessagesWidgetModel['eventBus'] {
        return MergedEventBus.from(
            EventBusBuilder.create()
                // Messages-specific events
                .addEvent('widget::instrumentChange', (payload) => {
                    this.setInstrument(parseInstrument(payload.instrument));
                })
                .addEvent('widget::updateStickyFilter', (_payload) => {
                    // Messages widget doesn't use sticky filter
                })
                .addEvent('widget::toggleStickyFilter', () => {
                    // Messages widget doesn't use sticky filter
                })
                .addEvent('widget::updateSelectedAccountIds', (payload) => {
                    this.store.dispatch(
                        updateWidget({
                            tabId: this.widgetData.tabId,
                            widgetUpdates: { accountIds: payload.accountIds },
                        }),
                    );
                })
                .addEvent('widget::updateSelectedAccountId', (payload) => {
                    // Messages uses multi-select, so convert single to array
                    this.store.dispatch(
                        updateWidget({
                            tabId: this.widgetData.tabId,
                            widgetUpdates: { accountIds: [payload.accountId] },
                        }),
                    );
                })
                .addEvent('widget::setIsAccountSelectionValid', (payload) => {
                    this.#isAccountSelectionValid.value = payload.isValid;
                })
                .addEvent('widget::changeOrderStatusFilter', (payload) => {
                    this.store.dispatch(
                        updateWidget({
                            tabId: this.widgetData.tabId,
                            widgetUpdates: { statusFilter: normalizeMessagesStatusFilter(payload.statusFilter) },
                        }),
                    );
                })
                .addEvent('widget::openGridSettingsDialog', (payload) => {
                    this.#isGridSettingsDialogOpen.value = payload.isOpen;
                })
                // Messages-specific signals
                .addSignal('widget::instrument', () => computed(() => instrumentToString(this.#instrument.value)))
                .addSignal('widget::allowedInstrumentTypes', () => this.#allowedInstrumentTypes)
                .addSignal('widget::selectedAccountIds', () =>
                    computed(() => this.#accountIds.value ?? (EmptyArray as string[])),
                )
                .addSignal('widget::isAccountSelectionValid', () => this.#isAccountSelectionValid)
                .addSignal('widget::orderStatusFilter', () => this.#statusFilterSignal)
                .addSignal('widget::selectableOrderStatuses', () => this.#selectableOrderStatuses)
                .addSignal(
                    'widget::connectionStatus',
                    () =>
                        computed(() => ({
                            status: this.#connectionStatus.value,
                            failureCount: 0,
                            progress: undefined,
                        })) satisfies ConnectionStatusSignal[keyof ConnectionStatusSignal],
                )
                .addSignal('widget::formattableGridSettings', () =>
                    computed(() => ({ settings: this.#formattingSettings.value, defaults: MessagesGridDefaults })),
                )
                .addSignal('widget::isGridSettingsDialogOpen', () => this.#isGridSettingsDialogOpen)
                .build(),
        )
            .merge(this.#channelSlice.eventBus)
            .merge(this.#widgetTabSlice.eventBus);
    }

    constructor(
        @inject('WidgetDataModel') @reacts private widgetData: ReactBindings['WidgetDataModel'],
        @inject('Store') private store: ReactBindings['Store'],
        @inject('TradingSocketService') tradingSocketService: TradingSocketService,
        @inject('ChannelSlice') channelSlice: ChannelSlice,
        @inject('WidgetTabSlice') widgetTabSlice: WidgetTabSlice,
        @inject('WorkspaceService') @optional() workspaceService?: WorkspaceService,
        @inject('TabId') @optional() tabId?: string,
    ) {
        // eslint-disable-next-line prefer-rest-params -- forwarding constructor args
        super(...arguments);

        this.#channelSlice = channelSlice;
        this.#widgetTabSlice = widgetTabSlice;
        this.#tradingSocketService = tradingSocketService;

        this.#connectionStatus = computed((): TradingSocketConnectionStatus => {
            const connected = this.#tradingSocketService.isConnected.value;
            const authenticated = this.#tradingSocketService.isAuthenticated.value;
            if (connected && authenticated) {
                return 'connected';
            }

            if (connected) {
                return 'connecting';
            }

            return 'disconnected';
        });

        if (tabId && workspaceService) {
            workspaceService.registerWidget(tabId, this);
            this.disposableStack.defer(() => workspaceService.deregisterWidget(tabId));
        }

        this.eventBus = this.#buildEventBus();
    }

    private get widget() {
        return this.widgetData.widget as MessagesWidgetViewModel;
    }

    get instrument(): ReadonlySignal<Instrument> {
        return this.#instrument as ReadonlySignal<Instrument>;
    }

    setInstrument(instrument: Instrument) {
        this.#instrument.value = instrument;
        this.store.dispatch(updateMessagesInstrument(this.widgetData.tabId, instrument));
    }

    init(tabId: string) {
        const selectWidgetData = makeSelectWidgetData(tabId);
        this.widgetData.init(tabId);

        // Initialize slices
        this.#channelSlice.init(tabId);
        this.#widgetTabSlice.init(tabId);
        this.disposableStack.use(this.#channelSlice);
        this.disposableStack.use(this.#widgetTabSlice);

        // Initialize header elements with feature flag check
        const shouldShowSymbolPicker = riskWidgetsShouldShowNewSymbolPicker.select(this.store.getState());
        this.headerElements = [
            ...COMMON_HEADER_ELEMENTS,
            { type: 'accounts', priority: 70, position: 60, selectType: 'multi', allAccountsLabel: 'my-activity' },
            { type: 'blotterStatusFilter', position: 70, priority: 60 },
            { type: 'gridSettingsDialog', position: 90, priority: 50 },
            { type: 'connectionStatus', position: 995, priority: 995, overflowPosition: 5 },
            ...(shouldShowSymbolPicker
                ? [
                      {
                          type: 'symbolPicker',
                          position: 25,
                          priority: 80,
                      } satisfies SymbolPickerElement,
                  ]
                : []),
        ];

        // Initialize signals
        this.#accountIds = signal(this.widget.accountIds ?? null);
        this.#statusFilterSignal = signal(normalizeMessagesStatusFilter(this.widget.statusFilter));
        this.#formattingSettings = signal(this.widget.formattingSettings ?? null);

        // Listen for widget updates
        const unsubscribe = this.store
            .subscribeSelector((state) => {
                // Check if the tab still exists before trying to select it
                const tabExists = state.tab.byId[tabId] !== undefined;
                if (!tabExists) {
                    // Tab has been removed, return null to indicate no update needed
                    return null;
                }
                return selectWidgetData(state);
            })
            .handle((widgetData) => {
                // Only update if widgetData is not null (tab still exists)
                if (widgetData !== null) {
                    const widget = widgetData.tab.widget as MessagesWidgetViewModel;
                    this.#instrument.value = widget.instrument || parseInstrument('');
                    this.#accountIds.value = widget.accountIds ?? null;
                    this.#statusFilterSignal.value = normalizeMessagesStatusFilter(widget.statusFilter);
                    this.#formattingSettings.value = widget.formattingSettings ?? null;
                }
            });

        this.disposableStack.defer(() => {
            unsubscribe();
        });
    }

    userSelectedSymbol(symbol: string) {
        this.store.dispatch(userDoubleClickedSymbolFromTable(this.widgetData.tabId, symbol));
    }
}
